/**
 * Mock draft.
 *
 * "What if Rajan takes the tight end at 3.04?" is the question every manager wants to play with
 * for a week beforehand, and the spreadsheet's answer has always been "make a copy of the tab".
 *
 * The trick here is that a mock does not need any new UI. It needs the real draft board pointed
 * at a throwaway store: same grid, same search, same keeper placement, same everything — except
 * that the actor is treated as privileged (you are simulating all ten teams, so of course you can
 * type anywhere) and nothing survives leaving the page.
 *
 * The seeding matters. A mock that starts empty is useless; it has to begin from the real state
 * — the published order, the submitted keepers, and any picks already made — so you are
 * simulating from where the league actually is.
 */

import { useEffect, useRef, useState } from 'react';
import { DraftBoard } from './DraftBoard.js';
import { MemoryStore } from '../store/memoryStore.js';
import type {
  DraftPick, DraftState, KeeperDeclaration, LeagueSettings, LeagueStore,
} from '../store/types.js';
import type { PlayerBank } from '../domain/playerBank.js';

const SEASON = 2026;

/** Everything a mock is built from, held so the seeding is inspectable while it is in use. */
interface Seed {
  state: DraftState | null;
  decls: KeeperDeclaration[];
  picks: DraftPick[];
  bank: PlayerBank | null;
  settings: LeagueSettings | null;
}

/**
 * `persist: false` is load-bearing, and it fixes a real leak. A default MemoryStore hydrates
 * from — and writes back to — the same localStorage key as the standalone app's REAL store, so
 * a mock's fake picks were being persisted: resetting rebuilt a fresh store that promptly
 * hydrated the picks it was supposed to discard, and in the standalone build a reload could
 * hand the mock's picks to the real board. A sandbox must never touch storage it shares.
 */
/**
 * The sandbox: a throwaway copy of the real board, with ONE deliberate hole in it.
 *
 * Everything here is `persist: false` because that is the point — you enter a hundred and sixty
 * picks, learn something, and throw them away. Mock KEEPERS are the exception. They are a
 * manager's read on the other nine teams, they take real effort to enter, and re-seeding or
 * closing the tab must not cost him them — so those two methods delegate to the live store while
 * everything around them stays disposable.
 *
 * The subscription is fanned out for the same reason: the board repaints on a sandbox event OR a
 * real mock-keeper event, otherwise a guess saved through here would not appear on the board
 * until the page was reloaded.
 */
async function sandboxFrom(seed: Seed, live: LeagueStore, me: string): Promise<MemoryStore> {
  const fresh = new MemoryStore({ persist: false });
  if (seed.settings) await fresh.saveSettings(seed.settings);
  if (seed.state) await fresh.saveDraftState(seed.state);
  for (const d of seed.decls) await fresh.saveDeclaration(d);
  for (const p of seed.picks) await fresh.savePick(p);
  if (seed.bank) await fresh.savePlayerBank(seed.bank);

  fresh.mockKeepers = (season, gm) => live.mockKeepers(season, gm);
  fresh.saveMockKeepers = (m) => live.saveMockKeepers(m);
  /*
   * THE PICKLIST IS READ THROUGH, AND WRITTEN THROUGH (Tim, 6 September: "using any players
   * added to the live picklist, but a view that's affected by the mock draft board instead",
   * then "allow adding to picklist with the star icon while in mock mode").
   *
   * A manager's queue belongs to the real draft, so it does not live in the sandbox — but the
   * rehearsal is where he finds out he wants somebody, and a star pressed there had nowhere to
   * go. Both ends point at the live store: he sees the same list on both boards, minus whoever
   * this rehearsal has taken, and a name he stars in here is on it when he switches back.
   *
   * What the sandbox must NOT do is edit the list on its own. The automatic pruning that follows
   * a real pick stays live-only (see `DraftBoard`), so nothing a mock does to a player takes him
   * off the list he is on.
   */
  fresh.pickListFor = (season, gm) => live.pickListFor(season, gm);
  fresh.savePickList = (list) => live.savePickList(list);
  /*
   * AND THE FARM LIST, FOR THE SAME REASON. There is no mock farm auction — the Farm rounds tab
   * shows the real one with every control off — but the list a man keeps for it is his own, not
   * the auction's, and a star pressed while the switch is on has the same one place to go.
   */
  fresh.farmTargetsFor = (season, gm) => live.farmTargetsFor(season, gm);
  fresh.saveFarmTargets = (t) => live.saveFarmTargets(t);
  /* Pick ownership is the ledger plus settled trades, and settled trades are the league's — read
     through to the live store so a mock never simulates a pick somebody has already sold. */
  fresh.liveTrades = (season) => live.liveTrades(season);

  /*
   * ONE DECLARATION CAN LEAVE THE SANDBOX: his own.
   *
   * A manager works out his slate in here — that is half of what the mock is for — and the result
   * is worth keeping. So a save of HIS declaration writes through to the real store as well as to
   * the sandbox; everybody else's stays local, which is what keeps the other nine teams a
   * hypothesis rather than an edit to the league.
   *
   * Installed AFTER the seeding loop above on purpose. Seeding calls `saveDeclaration` ten times
   * with the real slates, and with the delegation in place that would write every manager's own
   * declaration straight back at him on entering a mock.
   */
  const seedSave = fresh.saveDeclaration.bind(fresh);
  fresh.saveDeclaration = async (d) => {
    await seedSave(d);
    if (d.gm.toUpperCase() === me.toUpperCase()) await live.saveDeclaration(d);
  };
  /**
   * AND THE DRAFT ORDER FOLLOWS THE LEAGUE, without a reset.
   *
   * The seed froze `draftState` at the moment the sandbox was built, so a mock opened before the
   * order was published went on simulating the projected board afterwards — a draft in the wrong
   * seats, which is every conclusion the mock produces. Tim, 31 August: "the pick preferences ran
   * and set the draft order technically -- but the draftboard and mock draftboard should have
   * been updated to match." The live board picked it up from its subscription; the sandbox had no
   * way to hear.
   *
   * Only the league's half is adopted. `status` and `onTheClock` are the MOCK's own — it runs its
   * own clock and its own picks — so taking those from the live document would stop his run dead
   * the moment a commissioner touched anything.
   */
  const adoptOrder = async (season: number) => {
    const [theirs, mine] = await Promise.all([live.draftState(season), fresh.draftState(season)]);
    if (!theirs) return;
    const same = mine && mine.order.length === theirs.order.length
      && mine.order.every((g, i) => g.toUpperCase() === theirs.order[i]?.toUpperCase());
    if (same) return;
    await fresh.saveDraftState({
      ...theirs,
      status: mine?.status ?? theirs.status,
      onTheClock: mine?.onTheClock ?? theirs.onTheClock,
      updatedAt: new Date().toISOString(),
    });
  };

  const own = fresh.subscribe.bind(fresh);
  fresh.subscribe = (season, onChange) => {
    const offSandbox = own(season, onChange);
    /*
     * And once on subscribe, because a sandbox now survives the switch: an order published while
     * the man was on the live board arrives at no listener, and he would come back to a
     * rehearsal running in last week's seats.
     */
    void adoptOrder(season);
    const offLive = live.subscribe(season, (what) => {
      if (what === 'mockKeepers' || what === 'pickList' || what === 'farmTargets') onChange(what);
      /* The write lands in the sandbox, whose own subscription is what repaints the board. */
      if (what === 'draftState') void adoptOrder(season);
    });
    return () => { offSandbox(); offLive(); };
  };
  return fresh;
}

/*
 * THE SANDBOX OUTLIVES THE SWITCH (Tim, 6 September: "I mocked through future rounds but that
 * didn't persist when I toggled back and forth from live").
 *
 * The switch navigates — `/draft/board` and `/draft/mock` are two pages — so flipping it
 * unmounted this component, and coming back built a new MemoryStore and re-seeded it from the
 * real board. Twelve rounds of "what if Rajan takes the tight end" went with it, and looking at
 * the live board for five seconds is exactly what you do in the middle of a rehearsal.
 *
 * The store is held here instead, outside the component, keyed by the live store and the man
 * using it. `Reset to live draft` is the one thing that throws the picks away, which is what it
 * says on it. A page reload still starts clean: nothing in a sandbox is written to storage, and
 * that is deliberate (see `persist: false` above).
 */
let kept: {
  live: LeagueStore; gm: string; sandbox: MemoryStore; seed: Seed; generation: number;
} | null = null;
let genSeq = 0;

export function MockDraft(
  { gm, store, onGoLive }:
  { gm: string; store: LeagueStore; onGoLive?: () => void },
) {
  const reusable = kept && kept.live === store && kept.gm === gm ? kept : null;
  const sandbox = useRef<MemoryStore | null>(reusable?.sandbox ?? null);
  const seed = useRef<Seed | null>(reusable?.seed ?? null);
  const [ready, setReady] = useState(reusable !== null);
  const [generation, setGeneration] = useState(reusable?.generation ?? 0);

  /**
   * Reset the mock: throw the picks away and rebuild from the live board as it stands NOW.
   *
   * THIS WAS TWO BUTTONS AND SHOULD NOT HAVE BEEN. `Re-seed` re-fetched the real state; `Start
   * over` reused the seed already captured and only wiped the picks. The distinction was real —
   * "run it again from the same board" and "give me the board as it is now" are different
   * questions — but it was invisible: both sat side by side, both were two words, and both cost
   * you every pick you had entered, which is the part anybody actually weighs before pressing.
   *
   * So there is one, and it does the more useful half. Re-fetching during keeper season is the
   * point of a standing reset — another slate comes in most days, and a mock built on last
   * week's declarations is simulating a league that will not exist on the night.
   */
  async function reseed() {
    setReady(false);
    const [state, decls, picks, bank, settings] = await Promise.all([
      store.draftState(SEASON), store.declarations(SEASON), store.picks(SEASON),
      store.playerBank(SEASON), store.settings(SEASON),
    ]);
    seed.current = { state, decls, picks, bank, settings };
    sandbox.current = await sandboxFrom(seed.current, store, gm);
    genSeq += 1;
    kept = { live: store, gm, sandbox: sandbox.current, seed: seed.current, generation: genSeq };
    setGeneration(genSeq);
    setReady(true);
  }

  useEffect(() => {
    /* Back from the live board with the same store and the same man: pick the run up where it was. */
    if (kept && kept.live === store && kept.gm === gm) {
      sandbox.current = kept.sandbox;
      seed.current = kept.seed;
      setGeneration(kept.generation);
      setReady(true);
      return;
    }
    void reseed();
  }, [store, gm]);

  /*
   * The seed census the banner used to print — keepers seeded, slates outstanding, guesses made
   * — went with it. The caveat it carried is real (a board missing four slates simulates a
   * league that will not exist on the night) and it is still said where it can be acted on: the
   * Guess the keepers card in the rail names the teams whose slates are missing.
   */

  return (
    <>
      {/*
        THE MOCK BANNER IS GONE (Tim, 6 September). It spent a row of chrome on a census — how
        many keepers were seeded, from how many slates — that nobody acts on, above a page whose
        first job is to show a board. The one thing on it that did something, Reset, is now in
        the commissioner's bar beside Undo; the violet band under the mode row still says, in
        the drawing's own words, that nothing here is real.
      */}
      {ready && sandbox.current && (
        <DraftBoard
          key={generation}
          onGoLive={onGoLive}
          onResetMock={() => void reseed()}
          gm={gm}
          actor={gm}
          role="admin"
          mock
          store={sandbox.current}
        />
      )}
    </>
  );
}
